import React, { memo, useCallback, useMemo, useState } from 'react'
import { ControllerRenderProps } from 'react-hook-form'
import { useDebounce } from 'react-use'
import get from 'lodash/get'
import AutocompleteBase from 'transfix-ui/components/AutocompleteBase'
import { useAutocompleteLazyQuery } from 'transfix-ui/components/AutocompleteBase/hooks/useAutocompleteLazyQuery'
import { TAutocompleteBaseChangeHandler } from 'transfix-ui/components/AutocompleteBase/types'
import { IGetAutocompleteOptionsConfig } from 'transfix-ui/components/AutocompleteBase/utils/getAutocompleteOptions'
import { AutocompleteInputChangeReason } from 'transfix-ui/core'
import { ICarrierApplication } from 'types/graphqlTypes'
import { ICarrierResultFragment } from './graphql/CarrierResultFragment'
import { useSearchCarriersLazyQuery } from './graphql/SearchCarriers'

const DEBOUNCE_AMOUNT = 250

type TLabelGenerator = (carrier: ICarrierResultFragment) => string

const generateCarrierOption = (carrier: ICarrierResultFragment, getText?: TLabelGenerator) => {
  const { dotNumber, id, mcNumber, name } = carrier
  return {
    id,
    text: getText ? getText(carrier) : `MC ${mcNumber} · DOT ${dotNumber} · ${name}`,
    carrier,
  }
}

export interface ICarrierAutosuggestAutocompleteProps
  extends Omit<
    React.ComponentProps<typeof AutocompleteBase>,
    | 'options'
    | 'defaultValue'
    | 'onChange'
    | 'value'
    | 'autoHighlight'
    | 'autoSelect'
    | 'openOnFocus'
    | 'selectOnFocus'
  > {}

export interface ICarrierAutosuggestProps extends ControllerRenderProps {
  carrier?: ICarrierResultFragment | null
  enableCreateCarrier?: boolean
  onCreateCarrier?: (id: string) => void
  onCarrierChange?: (carrier: ICarrierApplication | null) => void
  onClear?: () => void
  inputValue?: string
  onInputValueChange?: (input: string) => void
  optionLabelGenerator?: (carrier: ICarrierResultFragment) => string
  AutocompleteProps?: ICarrierAutosuggestAutocompleteProps
}

export interface ICreateCarrierOption {
  id: string
  text: string
  inputValue: string
}

export const CarrierAutosuggest = memo<ICarrierAutosuggestProps>(
  ({
    AutocompleteProps = {},
    carrier,
    enableCreateCarrier = false,
    onCreateCarrier,
    onCarrierChange,
    onChange,
    onClear,
    value,
    inputValue,
    optionLabelGenerator,
    onInputValueChange,
  }) => {
    const [searchValue, setSearchValue] = useState<string>('')

    const [fetchCarriers, { options = [], loading }] = useAutocompleteLazyQuery(
      useSearchCarriersLazyQuery,
      {},
      {
        value: carrier ? generateCarrierOption(carrier, optionLabelGenerator) : {},
        getOptionsConfig: data => {
          // Generate group of matching carriers from returned result set
          const { searchCarrierApplications: carriers = [] } = data || {}
          const matchingCarriers: IGetAutocompleteOptionsConfig<ICarrierResultFragment> = {
            options: carriers,
            groupIndex: 1,
            groupName: 'Carriers',
            mapOption: carrier => generateCarrierOption(carrier, optionLabelGenerator),
          }

          // Option for creating new carrier - Only shown if enableCreateCarrier is true
          const createOption: IGetAutocompleteOptionsConfig<ICreateCarrierOption> = {
            options: [
              {
                id: 'new-carrier',
                text: `Create carrier with new MC/DOT ${inputValue}`,
                inputValue: inputValue || '',
              },
            ],
            groupIndex: 0,
            optionType: 'withIcon',
            iconName: 'PlusIcon',
            dividerAfter: true,
            mapOption: option => option,
          }

          if (inputValue !== '' || value !== '') {
            return enableCreateCarrier && Number(inputValue) > 0
              ? [createOption, matchingCarriers]
              : matchingCarriers
          }
          return []
        },
      }
    )

    /**
     * Refetch when searchValue changes
     */
    useDebounce(
      () => {
        if (searchValue) {
          fetchCarriers({ variables: { searchTerm: searchValue } })
        }
      },
      DEBOUNCE_AMOUNT,
      [searchValue, fetchCarriers]
    )

    const changeHandler: TAutocompleteBaseChangeHandler = useCallback(
      (_, selectedItem) => {
        const optionId = get(selectedItem, 'id')
        const isCreatingNewCarrier = optionId === 'new-carrier'

        if (isCreatingNewCarrier && onCreateCarrier) {
          const newCarrierNumber = get(selectedItem, 'inputValue', '')
          onCreateCarrier(newCarrierNumber)
        } else if (!isCreatingNewCarrier) {
          onChange(optionId)
        }

        // If onCarrierChange function provided, send carrier object
        if (onCarrierChange) {
          const selectedCarrier = get(selectedItem, 'carrier', null)
          onCarrierChange(selectedCarrier)
        }
      },
      [onChange, onCarrierChange, onCreateCarrier]
    )

    const inputChangeHandler = useCallback(
      (event: React.ChangeEvent<{}>, value: string, reason: AutocompleteInputChangeReason) => {
        const hasValidValue = event || (!event && value.length)
        const isInputUpdate = value !== '' && reason === 'input'
        const isEmptyValue = value === ''

        if (hasValidValue && onInputValueChange) {
          // Set input value - inputValue is used to generate 'Create new carrier option'
          onInputValueChange(value)
        }
        if (hasValidValue && isInputUpdate) {
          // Refresh result set with value for searchTerm
          setSearchValue(value)
        } else if (hasValidValue && isEmptyValue && onClear) {
          onClear()
        }
      },
      [onClear, onInputValueChange]
    )

    const computedValue = useMemo(() => {
      return options.find(({ id }) => id === value) || null
    }, [options, value])

    return (
      <AutocompleteBase
        {...AutocompleteProps}
        options={options}
        loading={loading}
        value={computedValue}
        onChange={changeHandler}
        inputValue={inputValue}
        onInputChange={inputChangeHandler}
        autoHighlight={false}
        autoSelect={false}
        openOnFocus={false}
        selectOnFocus={false}
      />
    )
  }
)

export default CarrierAutosuggest
